import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface DatePickerProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({ selectedDate, onDateSelect }) => {
  const [viewDate, setViewDate] = React.useState(new Date(selectedDate));
  
  const daysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };
  
  const firstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };
  
  const goToPreviousMonth = () => {
    const prevMonth = new Date(viewDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setViewDate(prevMonth);
  };
  
  const goToNextMonth = () => {
    const nextMonth = new Date(viewDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setViewDate(nextMonth);
  };
  
  const renderDaysOfWeek = () => {
    const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    return days.map(day => (
      <div key={day} className="w-10 h-10 flex items-center justify-center text-sm font-medium text-gray-600">
        {day}
      </div>
    ));
  };
  
  const renderDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysCount = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="w-10 h-10"></div>
      );
    }
    
    // Add cells for each day of the month
    for (let day = 1; day <= daysCount; day++) {
      const date = new Date(year, month, day);
      const isSelected = 
        date.getDate() === selectedDate.getDate() && 
        date.getMonth() === selectedDate.getMonth() && 
        date.getFullYear() === selectedDate.getFullYear();
      
      days.push(
        <button
          key={day}
          onClick={() => onDateSelect(date)}
          className={`w-10 h-10 flex items-center justify-center rounded-full text-sm
            ${isSelected 
              ? 'bg-emerald-600 text-white' 
              : 'hover:bg-gray-200 text-gray-800'
            }
          `}
        >
          {day}
        </button>
      );
    }
    
    return days;
  };
  
  const formatMonth = () => {
    return viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  };
  
  return (
    <div className="p-4 bg-white rounded-lg shadow-lg w-80">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPreviousMonth}
          className="p-1 rounded-full hover:bg-gray-200"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h2 className="text-lg font-semibold">{formatMonth()}</h2>
        <button
          onClick={goToNextMonth}
          className="p-1 rounded-full hover:bg-gray-200"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {renderDaysOfWeek()}
        {renderDays()}
      </div>
    </div>
  );
};

export default DatePicker;